import React, { useState, useEffect } from 'react';
import { Box, Typography, TextField, MenuItem, Card, CardContent, Grid, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Pagination, InputAdornment, Chip, IconButton } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import api from '../../api';

const MessageAuditSection = () => {
  const [channel, setChannel] = useState('news');
  const [messages, setMessages] = useState([]);
  const [selectedCommId, setSelectedCommId] = useState('');
  
  const [stats, setStats] = useState({ delivered: 0, read: 0, unread: 0, flagged: 0 });
  const [employees, setEmployees] = useState([]);
  
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    fetchMessages();
    setSelectedCommId('');
    setStats({ delivered: 0, read: 0, unread: 0, flagged: 0 });
    setEmployees([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channel]);

  useEffect(() => {
    if (selectedCommId) {
      fetchMessageStats();
      fetchEmployeeList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCommId, page, search, statusFilter]);

  const fetchMessages = async () => {
    try {
      const res = await api.get(`/communications/admin/channels/${channel}/messages`);
      setMessages(res.data);
    } catch (err) {
      console.error("Failed to fetch messages", err);
    }
  };

  const fetchMessageStats = async () => {
    try {
      const res = await api.get(`/communications/admin/messages/${selectedCommId}/stats`);
      setStats(res.data);
    } catch (err) {
      console.error("Failed to fetch stats", err);
    }
  };

  const fetchEmployeeList = async () => {
    try {
      const res = await api.get(`/communications/admin/messages/${selectedCommId}/employees?search=${search}&status=${statusFilter}&page=${page}&limit=10`);
      setEmployees(res.data.data);
      setTotalPages(res.data.total_pages);
    } catch (err) {
      console.error("Failed to fetch employee list", err);
    }
  };

  const handleDelete = async () => {
    if (window.confirm("Are you sure you want to delete this message? It will be removed from all employee mailboxes.")) {
      try {
        await api.delete(`/communications/admin/messages/${selectedCommId}`);
        alert("Message deleted successfully.");
        setSelectedCommId('');
        fetchMessages();
      } catch (err) {
        console.error("Failed to delete message", err);
      }
    }
  };

  const StatCard = ({ title, value, color }) => (
    <Card elevation={0} sx={{ border: '1px solid #e2e8f0', bgcolor: color, textAlign: 'center', height: '100px', width: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
      <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
        <Typography variant="body2" color="text.secondary" fontWeight="bold" gutterBottom>{title}</Typography>
        <Typography variant="h4" fontWeight="bold" color="#10394f">{value}</Typography>
      </CardContent>
    </Card>
  );

  return (
    <Box>
      <Typography variant="h6" fontWeight="bold" mb={3} color="#10394f">Message Delivery Audit</Typography>
      
      {/* Filters & Actions */}
      <Box sx={{ display: 'flex', gap: 2, mb: 4, alignItems: 'center' }}>
        <TextField
          select
          label="Channel"
          size="small"
          value={channel}
          onChange={(e) => setChannel(e.target.value)}
          sx={{ minWidth: 200, bgcolor: 'white' }}
        >
          <MenuItem value="news">News</MenuItem>
          <MenuItem value="announcements">Announcements</MenuItem>
          <MenuItem value="notifications">Notifications</MenuItem>
        </TextField>

        <TextField
          select
          label="Select Message"
          size="small"
          value={selectedCommId}
          onChange={(e) => setSelectedCommId(e.target.value)}
          sx={{ flexGrow: 1, bgcolor: 'white' }}
          disabled={messages.length === 0}
        >
          {messages.map(msg => (
            <MenuItem key={msg.id} value={msg.id}>
              {msg.subject} ({new Date(msg.received_at).toLocaleDateString()})
            </MenuItem>
          ))}
        </TextField>

        <IconButton 
          color="error" 
          disabled={!selectedCommId}
          onClick={handleDelete}
          title="Delete Message"
          sx={{ border: '1px solid #f87171', bgcolor: 'white', '&:hover': { bgcolor: '#fef2f2' } }}
        >
          <DeleteOutlineIcon />
        </IconButton>
      </Box>

      {selectedCommId && (
        <>
          {/* Standardized Metrics Row */}
          <Grid container spacing={2} mb={4}>
            <Grid item xs={3}><StatCard title="Delivered" value={stats.delivered} color="#f8fafc" /></Grid>
            <Grid item xs={3}><StatCard title="Read" value={stats.read} color="#e8f5e9" /></Grid>
            <Grid item xs={3}><StatCard title="Unread" value={stats.unread} color="#ffebee" /></Grid>
            <Grid item xs={3}><StatCard title="Flagged" value={stats.flagged} color="#fff8e1" /></Grid>
          </Grid>

          {/* Employee Table & Sub-filters */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, alignItems: 'center' }}>
            <Typography variant="subtitle1" fontWeight="bold">Employee Read Status</Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                size="small"
                placeholder="Search Emp ID or Name..."
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
                }}
                sx={{ bgcolor: 'white', minWidth: 200 }}
              />
              <TextField
                select
                size="small"
                value={statusFilter}
                onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
                sx={{ minWidth: 120, bgcolor: 'white' }}
              >
                <MenuItem value="ALL">All Status</MenuItem>
                <MenuItem value="READ">Read</MenuItem>
                <MenuItem value="UNREAD">Unread</MenuItem>
                <MenuItem value="FLAGGED">Flagged</MenuItem>
              </TextField>
            </Box>
          </Box>

          <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: 2 }}>
            <Table size="small">
              <TableHead sx={{ bgcolor: '#f8fafc' }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>Emp ID</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Employee Name</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Read Date & Time</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold' }}>Flagged</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {employees.map((row) => (
                  <TableRow key={row.emp_id}>
                    <TableCell>{row.emp_id}</TableCell>
                    <TableCell>{row.employee_name}</TableCell>
                    <TableCell>
                      <Chip 
                        label={row.is_read ? 'Read' : 'Unread'} 
                        size="small" 
                        color={row.is_read ? 'success' : 'default'} 
                        variant={row.is_read ? 'filled' : 'outlined'}
                      />
                    </TableCell>
                    <TableCell>{row.is_read ? new Date(row.read_at).toLocaleString() : '--'}</TableCell>
                    <TableCell align="center">{row.is_flagged ? 'Yes' : 'No'}</TableCell>
                  </TableRow>
                ))}
                {employees.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 3, color: 'text.secondary' }}>No active employees found matching the filters.</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
            <Pagination count={totalPages} page={page} onChange={(e, value) => setPage(value)} color="primary" />
          </Box>
        </>
      )}
    </Box>
  );
};

export default MessageAuditSection;